import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ActionInput } from './ActionInput';
import { IntegrationService } from '../services/IntegrationService.mjs';
import { ActionService } from '@ignitesales/neuro-application/src/neuro-guide/services/ActionService.mjs';
import { useAnswers } from '@ignitesales/neuro-application/src/neuro-guide/hooks';
import { NeuroMessage } from '@ignitesales/neuro-application';

const IntegrationForm = ({ action, context = {}, onClose }) => {
  const populateInputWithContext = (input) => {
    if (input.value && typeof input.value === 'string' && input.value.includes('{{context.')) {
      const contextKey = input.value.match(/{{context\.(\w+)}}/)?.[1];
      if (contextKey && context[contextKey] !== undefined) {
        return { ...input, value: context[contextKey] };
      }
    }
    return input;
  };

  const [loading, setLoading] = useState(false);
  const [inputs, setInputs] = useState(() => {
    return (action.inputs || []).map(populateInputWithContext);
  });
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null); // 'success' or 'error'
  const answers = useAnswers();

  useEffect(() => {
    const loadIntegrationInputs = async () => {
      try {
        if (action.targetType === 'integration') {
          const integration = await IntegrationService.getIntegrationByName(action.targetId);
          if (integration?.inputs && integration.inputs[action.command]) {
            const integrationInputs = integration.inputs[action.command];

            setInputs(prevInputs => {
              const currentInputNames = new Set(prevInputs.map(i => i.name));
              const newInputs = integrationInputs
                .filter(i => !currentInputNames.has(i.name)) // Avoid duplicates
                .map(populateInputWithContext);

              return [...prevInputs, ...newInputs];
            });
          }
        }
      } catch (error) {
        console.error('Error loading integration inputs:', error);
      }
    };

    if (action) {
      loadIntegrationInputs();
    }
  }, [action, context]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      // Extract the trigger (assuming first button trigger for now)
      const trigger = action.triggers?.[0];

      if (!trigger) {
        throw new Error('No trigger found for this action');
      }

      // Build params from answers and context
      const params = await ActionService.extractActionParams(action, context);

      // Execute the action
      const result = await ActionService.triggerAction(action, trigger._id, params);

      // Show success message
      if (action.successMessageKey) {
        setMessage(action.successMessageKey);
        setMessageType('success');
      } else {
        setMessage('Action completed successfully');
        setMessageType('success');
      }

      // Close modal after delay
      setTimeout(() => {
        if (onClose) onClose();
      }, 2000);

    } catch (error) {
      console.error('Error executing action:', error);

      // Show error message
      if (action.errorMessageKey) {
        setMessage(action.errorMessageKey);
        setMessageType('error');
      } else {
        setMessage('An error occurred while executing the action');
        setMessageType('error');
      }
    } finally {
      setLoading(false);
    }
  };
  // Generate message key from action name (matching portal's generateMessageKey)
  const getMessageKey = (suffix) => {
    if (!action?.name) return null;
    const camelName = action.name.replace(/[^a-zA-Z0-9\s]/g, '').split(' ')
      .map((word, index) => index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
    return `action.${camelName}.${suffix}`;
  };

  return (
    <div className="integration-form">
      {action?.name && (
        <NeuroMessage
          id={getMessageKey('formTitle')}
          tagName="h2"
          className="integration-form__title"
          shouldHideIfNotFound={true}
        />
      )}
      {action?.name && (
        <NeuroMessage
          id={getMessageKey('formSubtitle')}
          tagName="p"
          className="integration-form__subtitle"
          shouldHideIfNotFound={true}
        />
      )}
      <form onSubmit={handleSubmit}>
        {inputs.filter(input => !input.hidden).map((input, index) => (
          <div key={index} className="integration-form__input">
            <ActionInput
              actionInput={input}
              disabled={loading}
            />
          </div>
        ))}

        {message && (
          <div className={`integration-form__message integration-form__message--${messageType}`}>
            <NeuroMessage id={message} shouldHideIfNotFound={false} tagName="p" fallback={message} />
          </div>
        )}

        <div className="integration-form__actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </button>
          {onClose && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

IntegrationForm.propTypes = {
  action: PropTypes.object.isRequired,
  context: PropTypes.object,
  onClose: PropTypes.func
};

export default IntegrationForm;



